import { useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useScroll, useMotionValueEvent, useReducedMotion } from "framer-motion";
import { Reveal, MaskedLine, ScrollMaskedLine } from "@/components/Reveal";
import api from "@/lib/api";
import { readingTime } from "@/lib/readingTime";
import { usePageTitle } from "@/lib/usePageTitle";

const TABS = [
  { key: "note", label: "Research Notes" },
  { key: "paper", label: "Position Papers" },
  { key: "publication", label: "Publications" },
];

const TERRITORIES = [
  {
    title: "Human Capability",
    text: "How skills, expertise, cognition and agency evolve in technology-rich environments.",
  },
  {
    title: "Organisations",
    text: "How technological capability interacts with structures, processes, decision-making and organisational resilience.",
  },
  {
    title: "Technology & Human Systems",
    text: "How AI, automation and digital infrastructures reshape relationships between people and systems.",
  },
  {
    title: "Society & Institutions",
    text: "How technological transformation interacts with institutions, participation, inequality and collective adaptation.",
  },
];

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const lerp = (a, b, t) => a + (b - a) * t;

const SCATTER = [
  [310, 100], [190, 140], [345, 180], [125, 220], [265, 250], [85, 110], [325, 300], [205, 340],
];
const MID = [
  [150, 440], [250, 420], [320, 490], [110, 520], [230, 540], [300, 580], [160, 600], [260, 620],
];
const CLUSTER = [
  [215, 720], [255, 690], [290, 730], [175, 755], [245, 765], [295, 775], [190, 800], [265, 815],
];
const CONT_EDGES = [[0, 1], [1, 2], [0, 3], [3, 4], [1, 4], [2, 5], [4, 6], [5, 7], [6, 7], [3, 6]];

function ThesisContinuity() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const [p, setP] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.9", "end 0.5"] });

  useMotionValueEvent(scrollYProgress, "change", (v) => setP(v));

  const t1 = clamp(p * 1.7 - 0.1);
  const t2 = clamp(p * 2.1 - 0.85);
  const edgesOp = clamp(p * 1.8 - 0.4) * 0.55;
  const ringOp = clamp(p * 2.3 - 1.1) * 0.7;
  const pos = SCATTER.map(([sx, sy], i) => [
    lerp(lerp(sx, MID[i][0], t1), CLUSTER[i][0], t2),
    lerp(lerp(sy, MID[i][1], t1), CLUSTER[i][1], t2),
  ]);

  return (
    <div ref={ref} className="absolute inset-0 pointer-events-none hidden md:block" aria-hidden="true" data-testid="thesis-continuity">
      <svg viewBox="0 0 400 980" preserveAspectRatio="xMidYMid slice" className="absolute right-0 top-0 h-full w-[38%] opacity-70">
        {CONT_EDGES.map(([a, b], i) => (
          <line
            key={i}
            x1={pos[a][0]}
            y1={pos[a][1]}
            x2={pos[b][0]}
            y2={pos[b][1]}
            stroke="#0F0F0F"
            strokeWidth="1"
            style={{ opacity: edgesOp }}
          />
        ))}
        <circle
          cx="235"
          cy="752"
          r="95"
          fill="none"
          stroke="#1E4D38"
          strokeWidth="1"
          strokeDasharray="3 6"
          style={{
            opacity: ringOp,
            transformBox: "fill-box",
            transformOrigin: "center",
            animation: reduced ? "none" : "spin-slow 48s linear infinite",
          }}
        />
        {pos.map(([x, y], i) => (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={i % 3 === 0 ? 2.8 : 2}
            fill={t2 > 0.7 && i < 4 ? "#1E4D38" : i % 4 === 0 ? "#1E4D38" : "#0F0F0F"}
            style={{
              opacity: 0.75,
              animation: reduced ? "none" : `drift ${6 + (i % 4)}s ease-in-out ${i * 0.4}s infinite`,
            }}
          />
        ))}
      </svg>
    </div>
  );
}

export default function Research() {
  usePageTitle("Research | Mentheris", "Mentheris research: the Technology-Human Disequilibrium programme, research notes and publications on emerging technological change.");
  const [tab, setTab] = useState("note");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/research?type=${tab}`)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab]);

  return (
    <main className="pt-24 md:pt-32" data-testid="research-page">
      <div className="relative">
        <ThesisContinuity />

        {/* Opening perspective */}
        <section className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 pb-14 md:pb-20">
          <h1 className="font-serif font-medium tracking-tighter leading-[0.98] text-5xl mt-6 max-w-5xl" data-testid="research-headline">
            <MaskedLine delay={0.25}>We research what change</MaskedLine>
            <MaskedLine delay={0.42}>
              sets in <span className="italic text-ember">motion</span>.
            </MaskedLine>
          </h1>

          <div className="grid md:grid-cols-12 gap-10 mt-14 md:mt-20">
            <Reveal delay={0.1} className="md:col-span-8 md:col-start-4">
              <p className="font-serif text-2xl md:text-3xl leading-[1.3] tracking-tight max-w-3xl" data-testid="research-opening">
                Technological change does not happen in isolation. It reshapes how people think
                and work, how capabilities develop, how organisations operate, and how
                institutions and societies respond.
              </p>
              <p className="mt-6 text-fog leading-relaxed max-w-2xl">
                Mentheris investigates these relationships: the patterns, tensions, dependencies
                and possibilities that emerge as technology becomes embedded in human systems.
              </p>
            </Reveal>
          </div>
        </section>

        {/* Research territory */}
        <section className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 py-14 md:py-20" data-testid="research-territory">
          <h2 className="font-serif text-[40px] tracking-tight max-w-3xl" data-testid="territory-heading">
            <ScrollMaskedLine>Understanding the relationships</ScrollMaskedLine>
            <ScrollMaskedLine delay={0.15}>around technological change.</ScrollMaskedLine>
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 text-fog leading-relaxed max-w-2xl">
              Our work explores how emerging technologies interact with human capability,
              organisational structures and wider social systems. We are particularly interested
              in what becomes visible when these elements evolve at different speeds, under
              different pressures and with different capacities to respond.
            </p>
          </Reveal>

          <div className="relative mt-14 md:mt-16 pl-10 md:pl-14 max-w-3xl" data-testid="territory-list">
            <motion.span
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-[5px] top-2 bottom-2 w-px bg-line origin-top"
              aria-hidden="true"
            />
            {TERRITORIES.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.1}>
                <div className="group relative py-7 first:pt-0 last:pb-0" data-testid={`territory-${i + 1}`}>
                  <span
                    className="absolute -left-10 md:-left-14 top-9 group-first:top-2 w-[11px] h-[11px] rounded-full border border-line bg-bone transition-colors duration-500 group-hover:bg-ember group-hover:border-ember"
                    aria-hidden="true"
                  />
                  <h3 className="font-serif text-2xl md:text-3xl tracking-tight uppercase group-hover:text-ember transition-colors duration-500">
                    {t.title}
                  </h3>
                  <p className="mt-3 text-fog leading-relaxed max-w-xl">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Emerging research framework */}
        <section className="relative z-10" data-testid="thd-flagship">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-14 md:py-20">
            <Reveal>
              <p className="text-xs tracking-[0.25em] uppercase text-fog">An emerging research framework</p>
              <span className="block w-10 h-[2px] bg-leaf mt-6" aria-hidden="true" />
            </Reveal>
            <div className="mt-8">
              <h2 className="font-serif text-[28px] tracking-tight leading-[1.2]">
                <ScrollMaskedLine>Technology-Human Disequilibrium</ScrollMaskedLine>
                <ScrollMaskedLine delay={0.15}>Framework</ScrollMaskedLine>
              </h2>
              <Reveal delay={0.2}>
                <p className="mt-8 text-lg text-ink/80 leading-relaxed max-w-2xl">
                  Technology-Human Disequilibrium examines the imbalance that can emerge when the
                  speed, complexity and integration of technological systems exceed the capacity
                  of people, organisations or institutions to adapt effectively.
                </p>
                <p className="mt-5 text-fog leading-relaxed max-w-2xl">
                  The research investigates how this imbalance forms, how it manifests and what it
                  means for human capability, technology adoption, decision-making, organisational
                  resilience and the value created through technological transformation.
                </p>
                <a
                  href="https://ssrn.com/abstract=6426239"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="thd-cta"
                  className="group inline-flex items-center gap-2 mt-10 text-sm tracking-wide link-editorial"
                >
                  Explore the research
                  <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </Reveal>
            </div>
          </div>
        </section>
      </div>

      <section data-testid="research-library" className="bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 pt-10 md:pt-14 pb-20 md:pb-28">
          <Reveal>
            <div className="flex items-center gap-4 mb-10">
              <p className="text-xs tracking-[0.25em] uppercase text-fog">Research published</p>
              <span className="block w-10 h-[2px] bg-leaf" aria-hidden="true" />
            </div>
          </Reveal>
          <div className="flex flex-wrap gap-2 md:gap-3 mb-12" role="tablist" data-testid="research-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                data-testid={`research-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-5 py-2.5 text-sm tracking-wide border transition-colors duration-300 ${
                  tab === t.key ? "bg-ink text-bone border-ink" : "border-line text-fog hover:text-ink hover:border-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-fog text-sm" data-testid="research-loading">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-fog text-sm border border-line p-10" data-testid="research-empty">
              Nothing published here yet. New entries are added regularly.
            </p>
          ) : (
            <div className="divide-y divide-line border-y border-line">
              {items.map((item, i) => (
                <Reveal key={item.id} delay={i * 0.05}>
                  <article className="group relative py-8 md:py-10 grid md:grid-cols-12 gap-4 md:gap-8 items-baseline" data-testid={`research-entry-${i}`}>
                    <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-leaf scale-y-0 group-hover:scale-y-100 origin-top transition-transform duration-500" aria-hidden="true" />
                    <span className="md:col-span-2 text-xs tracking-[0.2em] text-fog uppercase">
                      {item.date}
                      <span className="block mt-1 normal-case tracking-normal text-fog/70" data-testid={`research-entry-reading-time-${i}`}>
                        {readingTime(item.body || item.excerpt)} min read
                      </span>
                    </span>
                    <div className="md:col-span-7">
                      <h3 className="font-serif text-lg leading-snug group-hover:text-ember transition-colors duration-300">
                        <Link to={item.id === "8956f6ad-3e31-46c8-8fc2-32df2ec11ba6" ? "/research-note-01" : item.id === "38804163-1b6b-49fd-af42-b3de04527ddc" ? "/research-note-02" : `/research/${item.id}`} data-testid={`research-entry-link-${i}`}>{item.title}</Link>
                      </h3>
                      <p className="mt-2 text-sm text-fog leading-relaxed max-w-2xl">{item.excerpt}</p>
                      {item.link && (
                        <a href={item.link} target="_blank" rel="noopener noreferrer" data-testid={`research-entry-external-${i}`} className="group/ext inline-flex items-center gap-1.5 mt-3 text-xs tracking-[0.15em] uppercase text-fog hover:text-ember transition-colors duration-300">
                          External publication
                          <ArrowUpRight size={13} className="transition-transform duration-300 group-hover/ext:translate-x-0.5 group-hover/ext:-translate-y-0.5" />
                        </a>
                      )}
                    </div>
                    <div className="md:col-span-3 flex flex-wrap gap-2 md:justify-end">
                      {item.tags?.map((t) => (
                        <span key={t} className="text-[10px] tracking-[0.15em] uppercase border border-line px-3 py-1 text-fog">{t}</span>
                      ))}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
